import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX, FiChevronDown } from 'react-icons/fi';
import { GiCrystalBall } from 'react-icons/gi';
import { useTranslation } from '../../hooks/useTranslation';
import './Navbar.css';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [festivalsOpen, setFestivalsOpen] = useState(false);
  const location = useLocation();
  const festivalsRef = useRef(null);
  const { t, lang, toggleLanguage } = useTranslation();

  const horoscopePath = lang === 'ta' ? '/jathagam-in-tamil' : '/online-horoscope-generator';
  const matchMakingPath = lang === 'ta' ? '/jathagam-porutham-in-tamil-for-marriage' : '/match-making';

  const isHoroscopePage = ['/online-horoscope-generator', '/jathagam-in-tamil'].includes(location.pathname);
  const isMatchingPage = ['/match-making', '/jathagam-porutham-in-tamil-for-marriage'].includes(location.pathname);

  const festivalsSubmenu = [
    { path: '/hindu-festivals', label: t('nav.hinduFestivals') },
    { path: '/tamil-festivals', label: t('nav.tamilFestivals') },
    { path: '/telugu-festivals', label: t('nav.teluguFestivals') },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setFestivalsOpen(false);
  }, [location]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (festivalsRef.current && !festivalsRef.current.contains(e.target)) {
        setFestivalsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isFestivalsActive = festivalsSubmenu.some((item) => location.pathname === item.path);

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="navbar-container">
        <Link to="/" className="navbar-logo">
          <GiCrystalBall className="logo-icon" />
          <span className="logo-text">AstroEpanchang</span>
        </Link>

        <div className={`navbar-menu ${isOpen ? 'active' : ''}`}>
          <ul className="navbar-links">
            <li>
              <Link
                to="/"
                className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}
              >
                {t('nav.home')}
              </Link>
            </li>

            {/* Free Horoscope */}
            <li>
              <Link
                to={horoscopePath}
                className={`nav-link ${isHoroscopePage ? 'active' : ''}`}
              >
                {t('nav.freeHoroscope')}
              </Link>
            </li>

            {/* Zodiac Signs */}
            <li>
              <Link
                to="/zodiac"
                className={`nav-link ${location.pathname === '/zodiac' ? 'active' : ''}`}
              >
                {t('nav.zodiacSigns')}
              </Link>
            </li>

            {/* Match Making */}
            <li>
              <Link
                to={matchMakingPath}
                className={`nav-link ${isMatchingPage ? 'active' : ''}`}
              >
                {t('nav.matchMaking')}
              </Link>
            </li>

            {/* Festivals Dropdown */}
            <li className="nav-dropdown" ref={festivalsRef}>
              <button
                type="button"
                className={`nav-link nav-dropdown-toggle ${isFestivalsActive ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setFestivalsOpen(!festivalsOpen);
                }}
              >
                {t('nav.festivals')} <FiChevronDown className={`dropdown-arrow ${festivalsOpen ? 'open' : ''}`} />
              </button>
              <ul className={`nav-dropdown-menu ${festivalsOpen ? 'show' : ''}`}>
                {festivalsSubmenu.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`nav-dropdown-item ${location.pathname === item.path ? 'active' : ''}`}
                      onClick={() => setFestivalsOpen(false)}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </li>

            {/* 108 Divya Desams */}
            <li>
              <Link
                to="/108-divya-desams"
                className={`nav-link ${location.pathname.startsWith('/108-divya-desams') ? 'active' : ''}`}
              >
                108 Divya Desams
              </Link>
            </li>

            {/* Important Dates */}
            <li>
              <Link
                to="/important-dates"
                className={`nav-link ${location.pathname === '/important-dates' ? 'active' : ''}`}
              >
                {t('nav.importantDates')}
              </Link>
            </li>

            {/* Blog */}
            <li>
              <Link
                to="/blog"
                className={`nav-link ${location.pathname === '/blog' ? 'active' : ''}`}
              >
                {t('nav.blog')}
              </Link>
            </li>

            {/* Contact */}
            <li>
              <Link
                to="/contact"
                className={`nav-link ${location.pathname === '/contact' ? 'active' : ''}`}
              >
                {t('nav.contact')}
              </Link>
            </li>
          </ul>
        </div>

        <button className="nav-lang-toggle" onClick={toggleLanguage} title="Switch Language">
          {lang === 'en' ? 'த' : 'EN'}
        </button>

        <button
          className="navbar-toggle"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
        >
          {isOpen ? <FiX /> : <FiMenu />}
        </button>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="mobile-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
